import Swiper from 'swiper';
import EffectCarousel from './effect-carousel.esm.js';
import { EffectFade, Navigation, Pagination, Thumbs, Scrollbar, Autoplay, FreeMode } from 'swiper/modules';

class SwiperCarousel {
	constructor(config) {
		this.config = Object.assign({
			scope: '.swiper',
		}, config);

		this.elements = document.querySelectorAll(this.config.scope);

		if (this.elements.length > 0) {
			this.init();
		}
	}

	init() {
		// Initialiseer eerst normale swipers, dan delayed
		this.#initializeBySelector(`${this.config.scope}:not(.delay-init)`);
		this.#initializeBySelector(`${this.config.scope}.delay-init`);
		this.#initSliderControls();
	}

	#initializeBySelector(selector) {
		const elements = document.querySelectorAll(selector);
		elements.forEach(el => {
			this.#initialiseSingleObject(el);
		});
	}

	async #initialiseSingleObject(element) {
		const settings = this.#buildSettings(element);
		const container = element.closest(settings.wrapperEl);
		if (settings.minSlides > 0) {
			await this.#cloneSlides(element, settings.minSlides);
		}
		const swiper = this.#createSwiper(element, container, settings);
		this.#hideNavigationIfNeeded(element, swiper, settings);
	}

	#initSliderControls() {
		const controls = document.querySelectorAll('[data-slider-control]');

		controls.forEach(control => {
			control.addEventListener('click', (e) => {
				e.preventDefault();
				this.#toggleSlider(control);
			});
		});
	}

	#toggleSlider(control) {
		const targetSelector = control.getAttribute('data-slider-control');
		const wrapper = document.getElementById(targetSelector);
		const swiperElement = wrapper?.querySelector('.swiper');

		if (!swiperElement?.swiper) {
			console.warn(`Swiper not found for selector: ${targetSelector}`);
			return;
		}

		const swiper = swiperElement.swiper;

		if (swiper.autoplay.running) {
			swiper.autoplay.stop();
			control.classList.add('swiper-is-paused');
			control.classList.remove('swiper-is-playing');
		} else {
			swiper.autoplay.start();
			control.classList.add('swiper-is-playing');
			control.classList.remove('swiper-is-paused');
		}
	}

	#buildSettings(element) {
		const data = { ...this.#getDataAttributes(element) };
		delete data.config;

		let settings = Object.assign(this.#defaultSettings(), data);

		// Merge config object indien aanwezig
		const configAttr = element.getAttribute('data-config');
		if (configAttr) {
			try {
				const configData = JSON.parse(configAttr);
				settings = { ...settings, ...configData };
			} catch (e) {
				console.error('Invalid data-config JSON:', e);
			}
		}

		// Autoplay configuratie
		if (data.delay) {
			settings.autoplay = {
				delay: data.delay,
				pauseOnMouseEnter: true
			};
		}

		// Link thumb swiper indien aanwezig
		if (settings.thumbs?.swiper) {
			const thumbElement = document.querySelector(settings.thumbs.swiper);
			settings.thumbs.swiper = thumbElement?.swiper;
		}

		return settings;
	}

	#getDataAttributes(element) {
		const data = {};

		// Converteer alle data-* attributes naar camelCase object
		Array.from(element.attributes).forEach(attr => {
			if (attr.name.startsWith('data-')) {
				const key = attr.name
					.substring(5)
					.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

				// Probeer JSON te parsen, anders als string
				try {
					data[key] = JSON.parse(attr.value);
				} catch {
					data[key] = attr.value;
				}
			}
		});

		return data;
	}

	#createSwiper(element, container, settings) {
		let swiper;
		if (!container?.id) {
			const { navigation, ...settingsWithoutNav } = settings;
			swiper = new Swiper(element, settingsWithoutNav);
		} else {
			this.#updateNavigationSelectors(container, settings);
			swiper = new Swiper(element, settings);
		}

		if (settings.autoplay) {
			this.#observeAutoplayVisibility(container ?? element, swiper);
		}

		return swiper;
	}

	// Autoplay start pas zodra de swiper daadwerkelijk in beeld is, anders loopt
	// hij (onzichtbaar) al ronde(s) voor en botst de eerste zichtbare transitie
	// met de reveal-animatie (data-animate) van de sectie.
	#observeAutoplayVisibility(target, swiper) {
		if (!target || !swiper.autoplay) return;

		swiper.autoplay.stop();

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					swiper.autoplay.start();
				} else {
					swiper.autoplay.stop();
				}
			});
		}, { threshold: 0.25 });

		observer.observe(target);
	}

	#updateNavigationSelectors(container, settings) {
		const containerId = container.id;
		const selectors = {
			next: `swiper-button-next-${containerId}`,
			prev: `swiper-button-prev-${containerId}`,
			scrollbar: `swiper-scrollbar-${containerId}`,
			pagination: `swiper-pagination-${containerId}`
		};

		// Update navigation
		if (settings.navigation) {
			const nextEls = container.querySelectorAll(settings.navigation.nextEl);
			const prevEls = container.querySelectorAll(settings.navigation.prevEl);

			nextEls.forEach(el => el.classList.add(selectors.next));
			prevEls.forEach(el => el.classList.add(selectors.prev));

			settings.navigation.nextEl = `.${selectors.next}`;
			settings.navigation.prevEl = `.${selectors.prev}`;
		}

		// Update scrollbar
		if (settings.scrollbar) {
			const scrollbarEl = container.querySelector(settings.scrollbar.el);
			scrollbarEl?.classList.add(selectors.scrollbar);
			settings.scrollbar.el = `.${selectors.scrollbar}`;
		}

		// Update pagination
		if (settings.pagination) {
			const paginationEl = container.querySelector(settings.pagination.el);
			paginationEl?.classList.add(selectors.pagination);
			settings.pagination.el = `.${selectors.pagination}`;
		}
	}

	#hideNavigationIfNeeded(element, swiper, settings) {
		if (swiper.snapGrid.length <= 1) {
			const container = element.closest(settings.wrapperEl);
			container?.classList.add('swiper-hide-navigation');
		}
	}

	#defaultSettings() {
		return {
			wrapperEl: '.swiper-container',
			modules: [Navigation, Thumbs, Pagination, EffectFade, Scrollbar, Autoplay, FreeMode,EffectCarousel],
			slidesPerView: 'auto',
			grabCursor: true,
			watchSlidesProgress: true,
			scrollbar: {
				el: '.swiper-scrollbar',
				hide: false,
			},
			pagination: {
				el: '.swiper-pagination',
				clickable: true,
				renderBullet: (index, className) => `<span class="${className}"></span>`,
			},
			navigation: {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
				addIcons: false,
			},
			thumbs: {
				autoScrollOffset: 1,
				multipleActiveThumbs: false,
			}
		};
	}

	async #cloneSlides(element, minSlides, swiper) {
		const wrapper = element.querySelector('.swiper-wrapper');

		if (!wrapper) {
			return;
		}

		const slides = wrapper.querySelectorAll('.swiper-slide');
		const slideCount = slides.length;
		const cloneCount = Math.ceil(minSlides / slideCount);
		const originalHtml = wrapper.innerHTML;

		await new Promise((resolve, reject) => {
			for (let i = 1; i < cloneCount; i++) {
				wrapper.insertAdjacentHTML('beforeend', originalHtml);
			}
			let requiredAmount = cloneCount * slideCount;

			let checkSlides = () => wrapper.querySelectorAll('.swiper-slide').length == requiredAmount;

			let valid = false;
			let count = 0;

			while (!valid) {
				if (count > 100) {
					console.error('Failed to clone slides in time');
					reject(false);
					return;
				}
				valid = checkSlides();
				count++;
			}

			resolve(true);
		});
	}

	#mergeValue(target, source) {
		if (Array.isArray(source)) {
			if (!Array.isArray(target)) {
				return [...source];
			}

			return source.map((value, i) => this.#mergeValue(target[i], value));
		}

		if (source && typeof source === 'object') {
			if (target && typeof target === 'object') {
				for (const [key, value] of Object.entries(source)) {
					target[key] = this.#mergeValue(target[key], value);
				}
				return target;
			}
			return { ...source };
		}

		return source ?? target;
	}
}

export default SwiperCarousel;